import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Sparkles } from 'lucide-react';
import { EmptyState } from '@/client/components/ui/empty-state';
import { Seo } from '@/client/components/Seo';
import { api } from '@/client/lib/api';
import { isLocale, type Locale } from '@/client/i18n';
import type { CoursePublicListItem } from '@/client/types/courses';

export function CoursesList() {
  const { t } = useTranslation();
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';

  const list = useQuery({
    queryKey: ['public', 'courses'],
    queryFn: () => api<CoursePublicListItem[]>('/api/v1/courses'),
  });

  return (
    <div>
      <Seo
        title={t('seo.courses_title')}
        description={t('seo.courses_description')}
        locale={locale}
      />

      {/* Hero */}
      <section className="relative">
        <div
          className="absolute top-10 -left-32 w-[26rem] h-[26rem] rounded-full pointer-events-none animate-float-orb"
          style={{
            background: 'radial-gradient(circle, rgba(193,167,226,0.40) 0%, transparent 65%)',
            filter: 'blur(40px)',
          }}
          aria-hidden
        />
        <div
          className="absolute top-32 -right-20 w-[28rem] h-[28rem] rounded-full pointer-events-none animate-float-orb"
          style={{
            background: 'radial-gradient(circle, rgba(117,59,189,0.18) 0%, transparent 65%)',
            filter: 'blur(48px)',
            animationDelay: '-12s',
          }}
          aria-hidden
        />
        <div className="relative mx-auto max-w-5xl px-5 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-12 text-center">
          <span className="inline-flex items-center gap-2 rounded-pill bg-white/70 backdrop-blur-md border border-glass-border-strong px-4 py-1.5 font-sans text-caption uppercase tracking-[0.18em] text-primary animate-fade-up shadow-glass-soft">
            <Sparkles size={12} />
            {t('courses.page_eyebrow', { defaultValue: 'Autorské kurzy' })}
          </span>
          <h1
            className="mt-5 font-display text-[clamp(2.25rem,6vw,4rem)] leading-[1.05] tracking-tight text-primary-dark animate-fade-up"
            style={{ animationDelay: '70ms' }}
          >
            {t('courses.page_title')}
          </h1>
          <p
            className="mt-5 font-display text-body-large text-text-secondary max-w-2xl mx-auto leading-relaxed animate-fade-up"
            style={{ animationDelay: '140ms' }}
          >
            {t('courses.page_intro')}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 sm:px-6 lg:px-8 pb-24">
        {list.isLoading && (
          <div className="grid gap-5 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="dr-card-static h-[26rem] animate-shimmer"
                style={{
                  background:
                    'linear-gradient(90deg, rgba(255,255,255,0.6), rgba(255,255,255,0.95), rgba(255,255,255,0.6))',
                  backgroundSize: '200% 100%',
                  animationDelay: `${i * 80}ms`,
                }}
              />
            ))}
          </div>
        )}

        {list.data && list.data.length === 0 && (
          <EmptyState
            variant="page"
            title={t('courses.list_empty_title')}
            description={t('courses.list_empty_body')}
          />
        )}

        {list.data && list.data.length > 0 && (
          <div className="grid gap-5 sm:gap-6 md:grid-cols-2 lg:grid-cols-3 stagger-children">
            {list.data.map((course) => (
              <CourseCard key={course.id} course={course} locale={locale} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function CourseCard({ course, locale }: { course: CoursePublicListItem; locale: Locale }) {
  const { t } = useTranslation();
  const hasLaunch =
    course.launchPriceCents !== null &&
    course.launchSeatsTotal > 0 &&
    course.launchSeatsRemaining > 0;
  const link = `/${locale}/kurzy/${course.slug}`;
  return (
    <Link
      to={link}
      className="group dr-card flex flex-col overflow-hidden hover:-translate-y-1"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        {course.thumbnailUrl ? (
          <img
            src={course.thumbnailUrl}
            alt=""
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div
            className="h-full w-full"
            style={{
              background:
                'linear-gradient(135deg, rgba(193,167,226,0.55) 0%, rgba(117,59,189,0.35) 100%)',
            }}
          />
        )}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, transparent 50%, rgba(45,21,72,0.45) 100%)',
          }}
          aria-hidden
        />
        {hasLaunch && (
          <span
            className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-pill px-3 py-1 font-sans text-caption font-medium text-white"
            style={{
              background: 'linear-gradient(135deg, #753bbd, #512d6d)',
              boxShadow: '0 4px 14px rgba(117,59,189,0.45)',
            }}
          >
            <Sparkles size={11} />
            {t('courses.launch_badge')}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-6">
        <h2 className="font-display text-h3 text-primary-dark">{course.title}</h2>
        <p className="mt-2 font-sans text-body text-text-secondary line-clamp-3 leading-relaxed">
          {course.promise}
        </p>
        {course.durationLabel && (
          <p className="mt-3 font-sans text-caption uppercase tracking-[0.14em] text-primary">
            {t('courses.duration_label', { label: course.durationLabel })}
          </p>
        )}
        <div className="mt-auto pt-6 flex items-end justify-between gap-4">
          <PriceBlock course={course} />
          <span className="inline-flex items-center gap-1 font-sans text-body-small font-medium text-primary group-hover:gap-2 transition-all">
            {course.enrolled ? t('courses.card_continue') : t('courses.card_view')}
            <ArrowRight size={14} />
          </span>
        </div>
      </div>
    </Link>
  );
}

function PriceBlock({ course }: { course: CoursePublicListItem }) {
  const { t } = useTranslation();
  const hasLaunch =
    course.launchPriceCents !== null &&
    course.launchSeatsTotal > 0 &&
    course.launchSeatsRemaining > 0;
  const formatter = new Intl.NumberFormat('sk-SK', {
    style: 'currency',
    currency: course.currency,
    minimumFractionDigits: 0,
  });
  if (hasLaunch && course.launchPriceCents !== null) {
    return (
      <div>
        <p className="font-sans text-caption uppercase tracking-[0.14em] text-primary/80">
          {t('courses.price_from')}
        </p>
        <p className="font-display text-h3 text-primary-dark leading-none">
          {formatter.format(course.launchPriceCents / 100)}
        </p>
        <p className="mt-0.5 font-sans text-caption text-text-muted line-through">
          {formatter.format(course.priceCents / 100)}
        </p>
      </div>
    );
  }
  return (
    <p className="font-display text-h3 text-primary-dark leading-none">
      {formatter.format(course.priceCents / 100)}
    </p>
  );
}
